/**
 * Outpainting 전용 Zustand 스토어 — Expand 모드.
 *
 * Why:
 * - 진행 중 잡 추적: 다이얼로그를 닫고 백그라운드 실행을 허용하기 위해 진행 상태를 전역에 유지
 * - 결과 캐시: 동일 jobId 재조회 시 네트워크 라운드트립 절감
 * - 매핑 state: Step 2 에서 사용자가 결과 1장을 어떤 인쇄부 background 로 적용할지 선택
 *
 * 다이얼로그 단계:
 *   0 — 인트로 (자켓 미리보기 + 선택적 prompt 입력)
 *   1 — 추론 진행 (폴링)
 *   2 — 결과 매핑 (1장 미리보기 + 6 인쇄부 체크박스)
 */
import { create } from 'zustand';
import { PrintUnitType } from '../types/enums';

export type OutpaintingJobStatus = 'pending' | 'processing' | 'done' | 'failed';

export interface OutpaintingJob {
  jobId: string;
  albumId: string;
  status: OutpaintingJobStatus;
  /** 0 ~ 100 */
  progress: number;
  result?: {
    url: string;
    fileId?: string;
  };
  error?: string;
  createdAt: number;
}

/** Step 2 매핑 기본값 — 6 인쇄부 모두 선택. */
export const DEFAULT_SELECTED_PRINT_UNIT_TYPES: PrintUnitType[] = [
  PrintUnitType.FRONT,
  PrintUnitType.SIDE,
  PrintUnitType.BACK_TOP,
  PrintUnitType.BACK,
  PrintUnitType.DISC2,
  PrintUnitType.DISC_SIDE,
];

interface OutpaintingState {
  /** jobId 별 진행 상태 — Map 직렬화 회피 위해 Record */
  jobs: Record<string, OutpaintingJob>;
  /** 다이얼로그 열림 상태 */
  dialogOpen: boolean;
  /** 다이얼로그 단계 — 0:인트로, 1:추론 진행, 2:결과 매핑 */
  dialogStep: 0 | 1 | 2;
  /** 현재 다이얼로그가 추적 중인 jobId (단일) */
  activeJobId: string | null;
  /** 사용자가 입력한 prompt */
  prompt: string;
  /** Step 2 매핑 — 결과 1장을 적용할 인쇄부 선택 (default 6개 모두). */
  selectedTypes: PrintUnitType[];

  // Actions
  upsertJob: (job: OutpaintingJob) => void;
  updateJobStatus: (
    jobId: string,
    patch: Partial<Omit<OutpaintingJob, 'jobId' | 'createdAt'>>
  ) => void;
  removeJob: (jobId: string) => void;
  setActiveJob: (jobId: string | null) => void;

  setPrompt: (prompt: string) => void;

  openDialog: () => void;
  closeDialog: () => void;
  setStep: (step: 0 | 1 | 2) => void;

  toggleSelectedType: (type: PrintUnitType) => void;
  setSelectedTypes: (types: PrintUnitType[]) => void;
  resetSelectedTypes: () => void;
}

export const useOutpaintingStore = create<OutpaintingState>((set) => ({
  jobs: {},
  dialogOpen: false,
  dialogStep: 0,
  activeJobId: null,
  prompt: '',
  selectedTypes: [...DEFAULT_SELECTED_PRINT_UNIT_TYPES],

  upsertJob: (job) =>
    set((state) => ({
      jobs: { ...state.jobs, [job.jobId]: job },
    })),

  updateJobStatus: (jobId, patch) =>
    set((state) => {
      const existing = state.jobs[jobId];
      if (!existing) return state;
      return {
        jobs: {
          ...state.jobs,
          [jobId]: { ...existing, ...patch },
        },
      };
    }),

  removeJob: (jobId) =>
    set((state) => {
      const next = { ...state.jobs };
      delete next[jobId];
      return { jobs: next };
    }),

  setActiveJob: (jobId) => set({ activeJobId: jobId }),

  setPrompt: (prompt) => set({ prompt }),

  openDialog: () =>
    set({
      dialogOpen: true,
      dialogStep: 0,
      selectedTypes: [...DEFAULT_SELECTED_PRINT_UNIT_TYPES],
    }),

  closeDialog: () => set({ dialogOpen: false }),

  setStep: (step) => set({ dialogStep: step }),

  toggleSelectedType: (type) =>
    set((state) => {
      const has = state.selectedTypes.includes(type);
      return {
        selectedTypes: has
          ? state.selectedTypes.filter((t) => t !== type)
          : [...state.selectedTypes, type],
      };
    }),

  setSelectedTypes: (types) => set({ selectedTypes: types }),

  resetSelectedTypes: () =>
    set({ selectedTypes: [...DEFAULT_SELECTED_PRINT_UNIT_TYPES] }),
}));
